import React, { FunctionComponent, useCallback, useLayoutEffect, useRef, useState } from 'react';

import cn from 'classnames';

import Button from '../../../../shared/components/button';
import SvgImage from '../../../../shared/components/svg-image';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { playMediaElement } from '../../../../shared/helpers/play-media-element';
import audioUrl from './background-audio.mp3';
import classes from './index.css';

const audioOnIcon = <SvgImage imageName="audioOn" />;
const audioOffIcon = <SvgImage imageName="audioOff" />;
const logger = createLogger('background-audio-button');

interface BackgroundAudioButtonProps {
    autoplay: boolean;
    className?: string;
}

const BackgroundAudioButton: FunctionComponent<BackgroundAudioButtonProps> = ({ className, autoplay }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<HTMLAudioElement>(null);
    const isPlayRequestedByUserRef = useRef(false);

    const getPlaybackAdditional = useCallback(() => {
        return {
            action: 'button_click',
            autoplay,
            currentTime: audioRef.current?.currentTime,
            pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
            paused: audioRef.current?.paused,
            readyState: audioRef.current?.readyState,
            surface: 'broadcast-background-audio',
        };
    }, [autoplay]);

    const onButtonClick = useCallback(() => {
        if (audioRef.current?.paused === false) {
            isPlayRequestedByUserRef.current = false;
            audioRef.current?.pause();
            countMetrika('stream_viewers_screen', 'music_off');
        } else if (audioRef.current?.paused === true) {
            isPlayRequestedByUserRef.current = true;
            playMediaElement(audioRef.current, (error) => {
                isPlayRequestedByUserRef.current = false;
                logger.warn(error, {
                    method: 'playAudioOnButtonClick',
                    additional: getPlaybackAdditional(),
                });
            });
        }
    }, [getPlaybackAdditional]);

    useLayoutEffect(() => {
        if (audioRef.current) {
            // При открытии страницы может не прийти (или мы не успеваем подписаться) первое событие playing.
            setIsPlaying(audioRef.current.paused === false);
        }
    }, []);

    const onPlay = useCallback(() => {
        setIsPlaying(true);

        if (isPlayRequestedByUserRef.current) {
            isPlayRequestedByUserRef.current = false;
            countMetrika('stream_viewers_screen', 'music_on');
        }
    }, []);

    const onPause = useCallback(() => {
        setIsPlaying(false);
    }, []);

    return (
        <>
            <Button
                className={cn(classes.BackgroundAudioButton, className)}
                pin="round-round"
                size="lg"
                view="neutral"
                onClick={onButtonClick}
                icon={isPlaying ? audioOnIcon : audioOffIcon}
            />
            <audio src={audioUrl} ref={audioRef} onPlay={onPlay} onPause={onPause} autoPlay={autoplay} loop />
        </>
    );
};

export default BackgroundAudioButton;
